import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Button,
  useTheme,
  IconButton,
  Divider,
  Paper,
  Stack,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  Public as PublicIcon,
  Message as MessageIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import { ExceptionEntityType } from "../../../../../../utils/enums";

const DetailRow = ({
  icon: Icon,
  label,
  value,
  isClickable = false,
  onClick = null,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
        py: 2,
        borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
        "&:last-child": {
          borderBottom: "none",
        },
      }}
    >
      <Icon
        sx={{
          color: theme.palette?.text?.secondary || "#666",
          fontSize: 20,
          mt: 0.5,
          flexShrink: 0,
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette?.text?.secondary || "#666",
            mb: 0.5,
            fontSize: "0.75rem",
            fontWeight: 600,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>
        <Box
          onClick={onClick}
          sx={{
            cursor: isClickable ? "pointer" : "default",
            "&:hover": isClickable
              ? {
                  "& .MuiTypography-root": {
                    color: theme.palette?.primary?.main,
                  },
                }
              : {},
          }}
        >
          {value}
        </Box>
      </Box>
    </Box>
  );
};

export const DeleteExceptionDialog = ({
  deleteDialogOpen,
  setDeleteDialogOpen,
  exceptionToDelete,
  submitLoading,
  handleConfirmDelete,
}) => {
  const theme = useTheme();

  const getEntityIcon = () => {
    if (exceptionToDelete?.employee_email) return PersonIcon;
    if (exceptionToDelete?.resource_id) return BadgeIcon;
    return PublicIcon;
  };

  const getEntityType = () => {
    if (exceptionToDelete?.employee_email) return "Employee";
    if (exceptionToDelete?.resource_id) return "Resource";
    return "Global";
  };

  const getEntityValue = () => {
    return (
      exceptionToDelete?.employee_email ||
      exceptionToDelete?.resource_id ||
      "All Employees"
    );
  };

  const getEntityLabel = () => {
    if (exceptionToDelete?.employee_email) return "Email";
    if (exceptionToDelete?.resource_id) return "Resource ID";
    return "Scope";
  };

  return (
    <Dialog
      open={deleteDialogOpen && exceptionToDelete}
      onClose={() => !submitLoading && setDeleteDialogOpen(false)}
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          minWidth: 600,
          maxWidth: 700,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: "1.25rem",
          color: theme.customColors.text.strong,
          background: theme.customColors.background.cardHeader,
          borderBottom: `1px solid ${theme.customColors.border.main}`,
          pb: 2,
          pt: 2,
          px: 3,
        }}
      >
        Delete Exception
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          pt: 3,
          pb: 0,
          "&.MuiDialogContent-root": {
            paddingTop: 3,
          },
        }}
      >
        {exceptionToDelete && (
          <>
            <DetailRow
              icon={CategoryIcon}
              label="Type"
              value={
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: theme.palette?.text?.primary,
                    textTransform: "capitalize",
                  }}
                >
                  {getEntityType()}
                </Typography>
              }
            />

            <DetailRow
              icon={getEntityIcon()}
              label={getEntityLabel()}
              value={
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: theme.palette?.text?.primary,
                    fontFamily: exceptionToDelete?.employee_email
                      ? "inherit"
                      : "monospace",
                    fontSize: exceptionToDelete?.employee_email
                      ? "inherit"
                      : "0.9rem",
                  }}
                >
                  {getEntityValue()}
                </Typography>
              }
            />

            <DetailRow
              icon={MessageIcon}
              label="Reason"
              value={
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    color: theme.palette?.text?.primary,
                    lineHeight: 1.5,
                  }}
                >
                  {exceptionToDelete?.reason}
                </Typography>
              }
            />
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          px: 3,
          pb: 2,
          pt: 2,
          borderTop: `1px solid ${theme.customColors.border.subtle}`,
          mt: 1,
        }}
      >
        <Button
          onClick={() => setDeleteDialogOpen(false)}
          disabled={submitLoading}
          variant="outlined"
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: "0.8125rem",
            px: 2,
            py: 0.75,
            borderRadius: 1,
            borderColor: theme.customColors.border.main,
            color: theme.customColors.text.strong,
            "&:hover": {
              borderColor: theme.customColors.border.strong,
              backgroundColor: theme.customColors.background.hover,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={async () => {
            await handleConfirmDelete();
            setDeleteDialogOpen(false);
          }}
          variant="contained"
          disabled={submitLoading}
          color="error"
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: "0.8125rem",
            px: 2,
            py: 0.75,
            borderRadius: 1,
            "&:disabled": {
              bgcolor: theme.customColors.background.light,
              color: theme.customColors.text.disabled,
            },
          }}
        >
          {submitLoading ? "Deleting..." : "Delete"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
